import { height, useHover } from "@netapp/bxp-design-system-react";
import classNames from "classnames";
import React, { forwardRef, useMemo, useRef, useState } from "react";
import { UnmountClosed } from 'react-collapse';
import { useTranslation } from "react-i18next";
import InlineSVG from "react-inlinesvg";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { allMenus as i18nNamespace } from "../../../../../i18n/namespace";
import leftNavStyles from "./CategoriesNav.module.scss";
import CategoryMenu from "./CategoryMenu";

const Category = React.memo(({ category, isOpen, isActive, open, onCategoryMenuItemClicked, selectedService }) => {
    const [isAnimationFinished, setAnimationFinished] = useState(false);
    const categoryRef = useRef(null);
    const { t } = useTranslation(i18nNamespace);
    const { hoverParentProps, isHovered } = useHover();

    if (isHovered && !isOpen) {
        open();
    }

    return <>
        <button {...hoverParentProps} onClick={open} ref={categoryRef} className={classNames(leftNavStyles.category, { [leftNavStyles['category-open']]: isOpen, [leftNavStyles['category-active']]: isActive })} data-nav-category={category.label}>
            <div className={leftNavStyles.icon}>
                {category.icon && <InlineSVG src={category.icon} />}
            </div>
            <div className={leftNavStyles['category-label']}>
                {t(category.label)}
            </div>
        </button>
        <UnmountClosed onWork={({ isFullyOpened }) => {
            setAnimationFinished(isFullyOpened);
        }} onRest={({ isFullyOpened }) => {
            setAnimationFinished(isFullyOpened);
        }} isOpened={isOpen} theme={{ collapse: height }}>
            <CategoryMenu isAnimationFinished={isAnimationFinished} services={category.services} onCategoryMenuItemClicked={onCategoryMenuItemClicked} selectedService={selectedService} />
        </UnmountClosed>
    </>
})

const Favourites = React.memo(({ onCategoryMenuItemClicked, isOpen, open, selectedService }) => {
    const favouritesList = useSelector(state => state.menu.favourites.list);
    const servicesByKey = useSelector(state => state.menu.servicesByKey);
    const { t } = useTranslation(i18nNamespace);

    const category = {
        label: t("Favorites"),
        services: favouritesList.map(favourite => {
            return servicesByKey[favourite];
        })
    };

    if (favouritesList.length === 0) {
        return "";
    }
    return <Category category={category} isOpen={isOpen} isActive={false} onCategoryMenuItemClicked={onCategoryMenuItemClicked} open={open} neverActive={true} selectedService={selectedService} />
});

const Home = React.memo(({ onCategoryMenuItemClicked, isOpen, open, selectedService }) => {
    const { t } = useTranslation(i18nNamespace);

    const category = {
        label: t("Home"),
        services: []
    };

    return <Category category={category} isOpen={isOpen} isActive={false} onCategoryMenuItemClicked={onCategoryMenuItemClicked} open={open} neverActive={true} selectedService={selectedService} />
});

const Categories = React.memo(forwardRef((props, lastNavRef) => {
    const ref = useRef(null);
    const [openCategory, setOpenCategory] = useState(null);
    const [selectedService, setSelectedService] = useState(null);
    const location = useLocation();
    const navItems = useSelector(state => state.menu.navItems);
    const servicesByTo = useSelector(state => state.menu.servicesByTo);
    const activeFeatures = useSelector(state => state.features.active);
    const [hasScroll, setHasScroll] = useState(false);
    const { t } = useTranslation(i18nNamespace);

    const activeCategory = useMemo(() => {
        if (!servicesByTo) {
            return;
        }

        const match = location.pathname.match(/(?<initialPath>\/[^/]+)/);
        if (!match?.groups?.initialPath) {
            return null;
        } else {
            const activeService = servicesByTo[match.groups.initialPath];
            setSelectedService(activeService);
            return activeService?.category;
        }
    }, [location.pathname, servicesByTo]);

    if (!navItems) {
        return <div />;
    }

    const menuLeave = () => {
        setOpenCategory(null);
    }

    return <>
        <div ref={ref} className={classNames(leftNavStyles.container, { [leftNavStyles['has-scroll']]: hasScroll })}
            onMouseLeave={menuLeave}
            onBlur={menuLeave}
        >
            <Favourites onCategoryMenuItemClicked={props.onCategoryMenuItemClicked} isOpen={openCategory === "Favorites"} open={() => setOpenCategory(openCategory === "Favorites" ? null : "Favorites")} selectedService={selectedService} />
            {process.env.REACT_APP_IS_AIDP !== "true" && <Home onCategoryMenuItemClicked={props.onCategoryMenuItemClicked} isOpen={openCategory === "Home"} open={() => setOpenCategory(openCategory === "Home" ? null : "Home")} selectedService={selectedService} />}
            <div className={leftNavStyles['nav-items']}>
                {navItems.map((category, index) => <Category
                    onCategoryMenuItemClicked={props.onCategoryMenuItemClicked}
                    key={category.label}
                    category={category}
                    isActive={activeCategory === category.label}
                    isOpen={(openCategory === category.label || activeFeatures.menuCategoriesOpen) && props.isShowingCategoriesNav}
                    open={() => setOpenCategory(openCategory === category.label ? null : category.label)}
                    selectedService={selectedService}
                />)}
            </div>
        </div>
        <button ref={lastNavRef} tabIndex={-1} aria-label={t("press_tab_key")}></button>
    </>
}));

export default forwardRef((props, lastNavRef) => {
    return <Categories ref={lastNavRef} onCategoryMenuItemClicked={props.onCategoryMenuItemClicked} isShowingCategoriesNav={props.isShowingCategoriesNav}/>
});
